/**
 * AgentElementRegistry
 *
 * Central registry for UI elements that the Platform Assistant can interact with.
 * Collects element registrations and computes the UI state snapshot on demand.
 */

import type {
  ElementRegistration,
  UIElement,
  UISection,
  UIState,
  UIElementItem,
} from '../types';

/**
 * Registry for managing UI element registrations.
 *
 * Responsibilities:
 * - Store element registrations from useAgentElement hooks
 * - Track parent-child relationships for hierarchy
 * - Compute visibility state using IntersectionObserver
 * - Generate complete UI state snapshot on demand
 */
export class AgentElementRegistry {
  private elements: Map<string, ElementRegistration> = new Map();
  private visibilityStates: Map<string, boolean> = new Map();
  private intersectionObserver: IntersectionObserver | null = null;
  private observedElements: Map<string, HTMLElement> = new Map();
  private pageType: string;

  constructor(pageType: string) {
    this.pageType = pageType;
    this.setupIntersectionObserver();
  }

  /**
   * Setup IntersectionObserver for visibility tracking
   */
  private setupIntersectionObserver(): void {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const elementId = entry.target.getAttribute('data-agent-element-id');
          if (elementId) {
            this.visibilityStates.set(elementId, entry.isIntersecting);
          }
        });
      },
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0.1, // 10% visible = visible
      }
    );
  }

  /**
   * Register an element
   */
  register(registration: ElementRegistration): void {
    this.elements.set(registration.id, registration);

    // Observe the DOM element for visibility if ref is provided
    if (registration.ref?.current) {
      this.observeElement(registration.id, registration.ref.current);
    }
  }

  /**
   * Unregister an element
   */
  unregister(elementId: string): void {
    this.elements.delete(elementId);
    this.visibilityStates.delete(elementId);

    // Stop observing
    const element = this.observedElements.get(elementId);
    if (element && this.intersectionObserver) {
      this.intersectionObserver.unobserve(element);
      this.observedElements.delete(elementId);
    }
  }

  /**
   * Update an existing registration
   */
  update(registration: ElementRegistration): void {
    const existing = this.elements.get(registration.id);
    if (existing) {
      this.elements.set(registration.id, { ...existing, ...registration });
    } else {
      this.register(registration);
    }
  }

  /**
   * Observe a DOM element for visibility changes
   */
  observeElement(elementId: string, element: HTMLElement): void {
    if (!this.intersectionObserver) return;

    // Set data attribute for identification in observer
    element.setAttribute('data-agent-element-id', elementId);

    // Unobserve old element if exists
    const oldElement = this.observedElements.get(elementId);
    if (oldElement && oldElement !== element) {
      this.intersectionObserver.unobserve(oldElement);
    }

    this.observedElements.set(elementId, element);
    this.intersectionObserver.observe(element);

    // Set initial visibility (assume visible until observer updates)
    if (!this.visibilityStates.has(elementId)) {
      this.visibilityStates.set(elementId, true);
    }
  }

  /**
   * Get visibility state for an element
   */
  isVisible(elementId: string): boolean {
    return this.visibilityStates.get(elementId) ?? true;
  }

  /**
   * Get all registered elements
   */
  getAllElements(): ElementRegistration[] {
    return Array.from(this.elements.values());
  }

  /**
   * Compute the complete UI state snapshot.
   * If any modal layer elements are registered, only modal elements are returned.
   * This ensures the AI focuses on the modal and doesn't see the page behind it.
   */
  computeUIState(): UIState {
    const sections = this.buildSectionHierarchy();

    return {
      page_type: this.pageType,
      timestamp: new Date().toISOString(),
      sections,
    };
  }

  /**
   * Determine which layer should be active.
   * If any modal layer root elements exist, use modal layer only.
   */
  private getActiveLayer(): 'page' | 'modal' {
    for (const element of this.elements.values()) {
      // Check if it's a root element (no parent) in modal layer
      if (!element.parentId && element.layer === 'modal') {
        return 'modal';
      }
    }
    return 'page';
  }

  /**
   * Build the hierarchical section structure from flat registrations.
   * Filters by active layer (modal takes precedence over page).
   */
  private buildSectionHierarchy(): UISection[] {
    const activeLayer = this.getActiveLayer();

    // Group elements by parent, filtering by layer
    const rootElements: ElementRegistration[] = [];
    const childrenMap: Map<string, ElementRegistration[]> = new Map();

    this.elements.forEach((element) => {
      const elementLayer = element.layer || 'page';

      // Only include elements from the active layer
      if (elementLayer !== activeLayer) {
        return;
      }

      if (!element.parentId) {
        rootElements.push(element);
      } else {
        const siblings = childrenMap.get(element.parentId) || [];
        siblings.push(element);
        childrenMap.set(element.parentId, siblings);
      }
    });

    // Convert root elements to sections
    return rootElements.map((element) =>
      this.elementToSection(element, childrenMap)
    );
  }

  /**
   * Convert an element registration to a UISection
   */
  private elementToSection(
    element: ElementRegistration,
    childrenMap: Map<string, ElementRegistration[]>
  ): UISection {
    const children = childrenMap.get(element.id) || [];

    return {
      id: element.id,
      type: element.type,
      visible: this.isVisible(element.id),
      children: children.map((child) =>
        this.elementToUIElement(child, childrenMap)
      ),
    };
  }

  /**
   * Convert an element registration to a UIElement (recursive)
   * Only includes non-empty arrays to reduce payload size
   */
  private elementToUIElement(
    element: ElementRegistration,
    childrenMap: Map<string, ElementRegistration[]>
  ): UIElement {
    const children = childrenMap.get(element.id) || [];

    const result: UIElement = {
      id: element.id,
      type: element.type,
      visible: this.isVisible(element.id),
      data: element.data || {},
    };

    // Only include non-empty arrays
    if (element.actions && element.actions.length > 0) {
      result.actions = element.actions;
    }
    if (element.item_actions && element.item_actions.length > 0) {
      result.item_actions = element.item_actions;
    }
    if (element.items && element.items.length > 0) {
      result.items = element.items as UIElementItem[];
    }
    if (children.length > 0) {
      result.children = children.map((child) =>
        this.elementToUIElement(child, childrenMap)
      );
    }

    return result;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.elements.clear();
    this.visibilityStates.clear();
    this.observedElements.clear();
  }
}
